"use client";

import { c as _c } from "react/compiler-runtime";
import { Bot, CheckCircle, ShieldCheck, XCircle } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useState } from "react";
import { AuthNavbar } from "@/components/auth-navbar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { authClient } from "@/lib/auth-client";
interface CapabilityGrantRequest {
  capability: string;
  constraints?: Record<string, unknown> | null;
  status?: string;
}
interface AgentInfo {
  agentId: string;
  agentName: string;
  mode: "delegated" | "autonomous" | string;
  hostId: string | null;
  status: string;
  requested: CapabilityGrantRequest[];
}
async function fetchAgent(agentId: string): Promise<AgentInfo | null> {
  const res = await fetch(`/api/auth/agent/get?agent_id=${encodeURIComponent(agentId)}`, {
    credentials: "include"
  });
  if (!res.ok) {
    return null;
  }
  const payload = (await res.json()) as Record<string, unknown>;
  const agent = payload.agent as Record<string, unknown> | undefined ?? payload;
  const requestedRaw = agent.capability_grants as CapabilityGrantRequest[] | undefined ?? agent.capabilityGrants as CapabilityGrantRequest[] | undefined ?? agent.pending_capabilities as CapabilityGrantRequest[] | undefined ?? [];
  return {
    agentId: String(agent.id ?? agentId),
    agentName: String(agent.name ?? "Unknown agent"),
    mode: String(agent.mode ?? "delegated"),
    hostId: agent.host_id as string | null ?? agent.hostId as string ?? null,
    status: String(agent.status ?? "pending"),
    requested: requestedRaw.filter(c => c && (c.status === "pending" || c.status === undefined))
  };
}
function ApprovalContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const agentId = searchParams.get("agent_id") ?? "";
  const userCode = searchParams.get("code") ?? searchParams.get("user_code") ?? "";
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [agent, setAgent] = useState<AgentInfo | null>(null);
  const [result, setResult] = useState<"approved" | "denied" | null>(null);
  useEffect(() => {
    if (!agentId) {
      setError("Missing agent_id parameter.");
      setLoading(false);
      return;
    }
    (async () => {
      const session = await authClient.getSession();
      if (!session.data?.user) {
        const returnTo = `/agent/approve?${searchParams.toString()}`;
        router.push(`/login?return_to=${encodeURIComponent(returnTo)}`);
        return;
      }
      const info = await fetchAgent(agentId);
      if (info) {
        setAgent(info);
      } else {
        setError("Could not load agent details. The approval request may have expired.");
      }
    })().catch(err => {
      console.error("Agent approval load error:", err);
      setError("Failed to load agent approval request.");
    }).finally(() => setLoading(false));
  }, [agentId, router, searchParams]);
  const submitAction = useCallback(async (action: "approve" | "deny") => {
    setActionLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth/agent/approve-capability", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          agent_id: agentId,
          user_code: userCode || undefined,
          action
        })
      });
      const payload = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        status?: string;
      };
      if (!res.ok || payload.error) {
        setError(payload.message || payload.error || `Failed to ${action} the capability request.`);
        return;
      }
      setResult(action === "approve" ? "approved" : "denied");
    } catch (err_0) {
      console.error("Agent approval submit error:", err_0);
      setError(`Failed to ${action} the capability request.`);
    } finally {
      setActionLoading(false);
    }
  }, [agentId, userCode]);
  if (loading) {
    return <div className="flex min-h-svh flex-col">
				<AuthNavbar />
				<div className="flex flex-1 items-center justify-center">
					<p className="text-muted-foreground">Loading agent request…</p>
				</div>
			</div>;
  }
  if (result) {
    return <div className="flex min-h-svh flex-col">
				<AuthNavbar />
				<div className="flex flex-1 items-center justify-center p-6 md:p-10">
					<Card className="w-full max-w-md">
						<CardContent className="flex flex-col items-center gap-4 pt-6">
							{result === "approved" ? <>
									<div className="rounded-full bg-green-100 p-3 dark:bg-green-900/30">
										<CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
									</div>
									<h2 className="font-semibold text-xl">
										Capabilities granted
									</h2>
									<p className="text-center text-muted-foreground">
										The agent can now execute the approved capabilities. You can
										close this window and return to the tool that initiated the
										request.
									</p>
								</> : <>
									<div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
										<XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
									</div>
									<h2 className="font-semibold text-xl">Request denied</h2>
									<p className="text-center text-muted-foreground">
										The agent will not be granted these capabilities. You can
										close this window.
									</p>
								</>}
						</CardContent>
					</Card>
				</div>
			</div>;
  }
  if (error || !agent) {
    return <div className="flex min-h-svh flex-col">
				<AuthNavbar />
				<div className="flex flex-1 items-center justify-center p-6 md:p-10">
					<Card className="w-full max-w-md">
						<CardContent className="pt-6">
							<Alert variant="destructive">
								<AlertDescription>
									{error || "Unable to load agent request."}
								</AlertDescription>
							</Alert>
						</CardContent>
					</Card>
				</div>
			</div>;
  }
  return <div className="flex min-h-svh flex-col">
			<AuthNavbar />
			<div className="flex flex-1 items-center justify-center p-6 md:p-10">
				<Card className="w-full max-w-lg">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 rounded-full bg-muted p-4">
							<Bot className="h-8 w-8 text-muted-foreground" />
						</div>
						<CardTitle className="text-2xl">Authorize agent</CardTitle>
						<CardDescription>
							{agent.agentName} is requesting capabilities on your account.
						</CardDescription>
					</CardHeader>
					<CardContent className="flex flex-col gap-6">
						{error && <Alert variant="destructive">
								<AlertDescription>{error}</AlertDescription>
							</Alert>}

						<div className="rounded-lg border bg-muted/50 p-4 text-sm">
							<div className="flex justify-between">
								<span className="text-muted-foreground">Agent mode</span>
								<span className="font-medium capitalize">{agent.mode}</span>
							</div>
							{userCode && <div className="mt-2 flex justify-between">
									<span className="text-muted-foreground">User code</span>
									<span className="font-mono">{userCode}</span>
								</div>}
						</div>

						<div>
							<div className="mb-2 flex items-center gap-2 font-medium text-sm">
								<ShieldCheck className="h-4 w-4" />
								Requested capabilities
							</div>
							{agent.requested.length === 0 ? <p className="text-muted-foreground text-sm">
									No pending capabilities — the agent may have already been
									granted everything it asked for.
								</p> : <ul className="space-y-2">
									{agent.requested.map(c => <li className="rounded-md border p-3 text-sm" key={c.capability}>
											<div className="font-mono">{c.capability}</div>
											{c.constraints && <pre className="mt-1 whitespace-pre-wrap text-muted-foreground text-xs">
													{JSON.stringify(c.constraints, null, 2)}
												</pre>}
										</li>)}
								</ul>}
						</div>

						<Alert>
							<AlertDescription>
								Only approve if you initiated this request in a tool you trust.
								Sigma Auth never holds your private keys — no capability here
								signs on your behalf.
							</AlertDescription>
						</Alert>

						<div className="flex gap-3">
							<Button className="flex-1" disabled={actionLoading} onClick={() => submitAction("deny")} variant="outline">
								{actionLoading ? "Processing…" : "Deny"}
							</Button>
							<Button className="flex-1" disabled={actionLoading} onClick={() => submitAction("approve")}>
								{actionLoading ? "Processing…" : "Approve"}
							</Button>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>;
}
export default function AgentApprovePage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<div className="flex min-h-screen items-center justify-center">Loading…</div>}><ApprovalContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}